
<template>
  <el-row :gutter="15">
    <el-col :span="24">
      <el-card
        v-loading="isLoading"
        shadow="never"
      >
        <SeedTable
          :loading="isLoading"
          :seeds="seeds"
          @edit="showForm"
        />
        <br>
        <div style="text-align: center;">
          <el-pagination
            :current-page.sync="seeds.page"
            :page-size.sync="seeds.limit"
            layout="total, sizes, prev, pager, next, jumper"
            :total="seeds.total"
            @size-change="reload"
            @current-change="reload"
          />
        </div>

        <el-dialog
          :title="dialogTitle"
          :visible.sync="isShowing"
          :close-on-click-modal="false"
          width="550px"
        >
          <el-form
            ref="seedFormRef"
            v-loading="isSaving"
            :model="seedForm"
            :rules="rules"
            label-position="top"
          >
            <el-form-item
              label="Carrier Event"
              prop="event"
            >
              <el-input v-model="seedForm.event" />
            </el-form-item>

            <el-form-item
              label="Carrier Description"
              prop="description"
            >
              <el-input
                v-model="seedForm.description"
                type="textarea"
                :rows="3"
              />
            </el-form-item>

            <el-form-item
              label="Tracking Level"
              prop="trackingLevelId"
            >
              <el-select
                v-model="seedForm.trackingLevelId"
                placeholder="Select tracking level"
                filterable
                style="width: 100%;"
              >
                <el-option
                  v-for="level in trackingLevels"
                  :key="level.id"
                  :label="levelLabel(level)"
                  :value="level.id"
                />
              </el-select>
            </el-form-item>

            <el-form-item>
              <el-checkbox v-model="seedForm.refreshEvents">
                Refresh Events
              </el-checkbox>
              <br>
              <small>This will refresh the events for the selected seed. This process may take a very long time depending on the number of events.</small>
            </el-form-item>
          </el-form>

          <template #footer>
            <el-button @click.native="isShowing = false">
              Cancel
            </el-button>

            <el-button
              type="primary"
              :loading="isSaving"
              @click.native="save"
            >
              {{ isEditing ? 'Update' : 'Create' }}
            </el-button>
          </template>
        </el-dialog>
      </el-card>
    </el-col>
  </el-row>
</template>
<script>
import SeedTable from '@/components/Application/Manage/Carriers/Tracking/SeedTable'

export default {
  components: {
    SeedTable
  },
  data () {
    return {
      title: 'Seeds',
      breadcrumb: {
        name: 'Seeds',
        route: { name: 'manage-carriers-carrier-tracking-seeds', params: { carrierId: this.$route.params.carrierId } }
      },
      actions: [
        {
          hidden: false,
          name: 'Create',
          icon: ['fal', 'plus'],
          event: this.showForm
        },
        {
          name: 'Refresh',
          icon: ['fal', 'sync'],
          event: this.reload
        }
      ],
      isLoading: true,
      isSaving: false,
      isShowing: false,
      carrier: null,
      seeds: [],
      trackingLevels: [],
      filters: [],
      seed: null,
      seedForm: {
        event: '',
        description: '',
        trackingLevelId: null,
        refreshEvents: false
      },
      rules: {
        event: [
          { required: true, message: 'Carrier event is required', trigger: 'blur' }
        ],
        trackingLevelId: [
          { required: true, message: 'Tracking level is required', trigger: 'change' }
        ]
      }
    }
  },
  computed: {
    isEditing () {
      return !!this.$_.get(this.seed, 'id')
    },
    dialogTitle () {
      return `${this.isEditing ? 'Edit' : 'Create'} Carrier Seed`
    }
  },
  created () {
    this.load()
  },
  methods: {
    async load () {
      this.isLoading = true

      await Promise.all([
        this.$sdk.carrier.read(this.$route.params.carrierId, request => {
          request.include = [
            'seeds.level.type'
          ]
        }).then(res => { this.seeds = res.seeds }),
        this.$sdk.tracking_level.list(builder => {
          builder.include = ['type']
          builder.order = 'name:asc'
          builder.limit = 1000
        }).then(res => { this.trackingLevels = res })
      ]).finally(() => { this.isLoading = false })
    },
    async reload () {
      try {
        this.isLoading = true
        this.$sdk.carrier.read(this.$route.params.carrierId, request => {
          request.include = [
            'seeds.level.type'
          ]
        }).then(res => { this.seeds = res.seeds })
      } catch (error) {
        console.error(error)
        this.$message.error('An error occurred while loading the seeds')
      } finally {
        this.isLoading = false
      }
    },
    levelLabel (level) {
      const typeName = this.$_.get(level, 'type.name')
      return typeName ? `${level.name} (${typeName})` : level.name
    },
    showForm (row) {
      if (row instanceof this.$sdk.seed) {
        this.seed = row.clone()
      } else {
        this.seed = new this.$sdk.seed()
      }

      this.seedForm = {
        event: this.$_.get(this.seed, 'event', ''),
        description: this.$_.get(this.seed, 'description', ''),
        trackingLevelId: this.$_.get(this.seed, 'level.id', null),
        refreshEvents: false
      }

      this.isShowing = true
      this.$nextTick(() => this.$refs.seedFormRef && this.$refs.seedFormRef.clearValidate())
    },
    async save () {
      const form = this.$refs.seedFormRef
      if (!form) return

      form.validate(async (valid) => {
        if (!valid) return

        this.isSaving = true

        try {
          this.seed.event = this.seedForm.event
          this.seed.description = this.seedForm.description
          this.seed.level = this.trackingLevels.find(level => level.id === this.seedForm.trackingLevelId) || null

          if (this.isEditing) {
            await this.seed.update(builder => {
              builder.context = { resource: 'carrier', id: this.$route.params.carrierId }
            })
          } else {
            await this.seed.create(builder => {
              builder.context = { resource: 'carrier', id: this.$route.params.carrierId }
            })
          }

          if (this.seedForm.refreshEvents) {
            await this.refreshEvents(this.seed)
          }

          this.$message({ message: 'Successfully saved', type: 'success' })
          this.isShowing = false
          await this.reload()
        } finally {
          this.isSaving = false
        }
      })
    },
    async refreshEvents (seed) {
      const job = new this.$sdk.job()
      job.name = 'seed.update'
      job.parameters = {
        seed: seed.id
      }
      job.create()
        .then(() => this.$message({ message: seed.event + ' Tracking Update Queued', type: 'success' }))
    }
  }
}
</script>
